// src/contexts/NavbarActionsContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

/**
 * NavbarActionsContext
 * -----------------------------------------------------------------------------
 * Lightweight bridge between route/page-level workflows and the shared app navbar.
 *
 * PURPOSE
 *  • Allow pages to publish route-specific navbar actions/state
 *  • Allow NavbarComponent to render those actions without prop drilling
 *  • Keep page logic inside the page and navbar rendering inside the navbar
 *
 * CURRENT USE CASE
 *  • SmartLists page-level Save action
 *  • SmartLists dirty state / save status in the global navbar
 *
 * IMPORTANT
 *  • This is NOT a general app-state dumping ground
 *  • This is NOT page business logic
 *  • This is NOT form-builder-specific
 *  • It is only a route-to-navbar action bridge
 *
 * DESIGN GOALS
 *  • Small surface area
 *  • Resettable when pages unmount
 *  • Safe defaults so NavbarComponent can render without special guards
 * -----------------------------------------------------------------------------
 */

const NavbarActionsContext = createContext(null);

const DEFAULT_ACTIONS = Object.freeze({
  pageKey: null,                // e.g. 'smartlists'
  primaryActionLabel: null,     // e.g. 'Save'
  primaryActionEnabled: false,
  primaryActionLoading: false,
  primaryActionOnClick: null,
  dirty: false,
  statusText: '',               // optional small helper text later
});

export const NavbarActionsProvider = ({ children }) => {
  const [actions, setActionsState] = useState(DEFAULT_ACTIONS);

  const setNavbarActions = useCallback((nextActions = {}) => {
    setActionsState((prev) => ({
      ...DEFAULT_ACTIONS,
      ...prev,
      ...nextActions,
    }));
  }, []);

  const resetNavbarActions = useCallback(() => {
    setActionsState(DEFAULT_ACTIONS);
  }, []);

  const value = useMemo(() => ({
    actions,
    setNavbarActions,
    resetNavbarActions,
  }), [actions, setNavbarActions, resetNavbarActions]);

  return (
    <NavbarActionsContext.Provider value={value}>
      {children}
    </NavbarActionsContext.Provider>
  );
};

export const useNavbarActions = () => {
  const context = useContext(NavbarActionsContext);

  if (!context) {
    return {
      actions: DEFAULT_ACTIONS,
      setNavbarActions: () => {},
      resetNavbarActions: () => {},
    };
  }

  return context;
};

export default NavbarActionsProvider;